import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ErrorBoundary, ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentProps } from 'bernie-l10n';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import {
  AllAmenitiesViewModel,
  GalleryViewModel,
  HotelMapModel,
  OverviewViewModel,
  PrimaryPageMessagingModel,
  PropertyAdditionalInfoViewModel,
  PropertyInfoPriceGuaranteeViewModel,
  ReviewsViewModel,
  ScrollTarget,
  ShortlistModel,
  StickyOfferModel
} from 'models';
import { VrPropertySummaryViewModel } from 'models/hotel/hotel-details-types';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';

// Components
import { HotelInfo } from 'components/infosite/property-details/hotel-info';
import { Information } from 'components/infosite/property-details/information';
import { InfositeGallery } from 'components/infosite/property-details/infosite-gallery';
import { PropertyBadges } from 'components/infosite/property-details/property-badges';

export type LocalizedPropertyDetailsProps = LocalizedComponentProps & PropertyDetailsProps;

export interface PropertyDetailsProps {
  hotelId: number;
  id: number;
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  additionalInfo?: PropertyAdditionalInfoViewModel;
  amenities?: AllAmenitiesViewModel;
  defaultData?: any;
  gallery?: GalleryViewModel;
  hotelName?: string;
  englishHotelName?: string;
  overview?: OverviewViewModel;
  reviews?: ReviewsViewModel;
  stars?: string;
  starRatingIcon?: string;
  shortlistModel?: ShortlistModel;
  primaryPageMessagingModel?: PrimaryPageMessagingModel;
  mapModel?: HotelMapModel;
  singleUnitVrPropertySummary?: VrPropertySummaryViewModel;
  //Begin Abacus: 28909_pwa_VIP_badges
  vipMessage?: string;
  //End Abacus: 28909_pwa_VIP_badges
  isRefreshedHISLayout: boolean;
  stickyOffer?: StickyOfferModel;
  priceGuarantee?: PropertyInfoPriceGuaranteeViewModel;
  roomsAndRatesTarget?: ScrollTarget;
  goBack: () => void;
  favoriteButton?: JSX.Element;
  propertyTypeBadge?: string; // Experiment: 32599 Property_Type_Badging
  propertyAffinities?: string;
}

@inject('routeStore', 'analytics', 'currentHotel', 'isRefreshedHISLayout')
@observer
export class PropertyDetails extends React.Component<LocalizedPropertyDetailsProps> {
  public static defaultProps: Partial<LocalizedPropertyDetailsProps> = {
    isRefreshedHISLayout: false
  };

  public render() {
    const {
      defaultData,
      additionalInfo,
      amenities,
      analytics,
      currentHotel,
      hotelId,
      hotelName,
      englishHotelName,
      gallery,
      id,
      mapModel,
      overview,
      primaryPageMessagingModel,
      reviews,
      routeStore,
      starRatingIcon,
      stars,
      singleUnitVrPropertySummary,
      vipMessage,
      isRefreshedHISLayout,
      stickyOffer,
      priceGuarantee,
      roomsAndRatesTarget,
      favoriteButton,
      propertyTypeBadge,
      propertyAffinities
    } = this.props;
    const galleryComponent = (
      <InfositeGallery
        analytics={analytics}
        currentHotel={currentHotel}
        gallery={gallery}
        hotelId={hotelId}
        hotelName={hotelName}
        id={id}
        routeStore={routeStore}
        favoriteButton={favoriteButton}
      />
    );

    const hotelInfo = (withName = true) => (
      <HotelInfo
        additionalInfo={additionalInfo}
        amenities={amenities}
        hotelName={hotelName}
        englishHotelName={englishHotelName}
        mapModel={mapModel}
        overview={overview}
        primaryPageMessagingModel={primaryPageMessagingModel}
        reviews={reviews}
        starRatingIcon={starRatingIcon}
        stars={stars}
        singleUnitVrPropertySummary={singleUnitVrPropertySummary}
        vipMessage={vipMessage}
        isRefreshedHISLayout={isRefreshedHISLayout}
        stickyOffer={stickyOffer}
        priceGuarantee={priceGuarantee}
        roomsAndRatesTarget={roomsAndRatesTarget}
        withName={withName}
        currentHotel={currentHotel}
        propertyTypeBadge={propertyTypeBadge}
        propertyAffinities={propertyAffinities}
      />
    );

    /**
     * This is a bit of a hack to set a loaded state once we have data back for these items - the class name indicates
     *  we can remove the min-height on the container that avoids display jank/bounciness.
     */
    const status: string = defaultData ? 'initialized' : 'loaded';
    const cardClassName = `property-details all-b-margin-three ${status}`;
    const largeCardClassName = `${cardClassName} imagelayout-left-fullbleed`;

    const smallView = (
      <UitkCard className={`${cardClassName} uitk-flat-border-top`}>
        {galleryComponent}
        <UitkCardContent>{hotelInfo()}</UitkCardContent>
      </UitkCard>
    );

    const largeView = (
      <UitkCard className={`${largeCardClassName} uitk-flat-border-top all-t-margin-two`}>
        <section className="uitk-cell uitk-card-media all-cell-2-3">{galleryComponent}</section>
        <div className="uitk-cell all-cell-1-3">{hotelInfo(false)}</div>
      </UitkCard>
    );

    const hotelBasicInfo = (
      <Information
        singleUnitVrPropertySummary={singleUnitVrPropertySummary}
        name={hotelName}
        englishName={englishHotelName}
        stars={stars}
        starRatingIcon={starRatingIcon}
        stickyOffer={stickyOffer}
        priceGuarantee={priceGuarantee}
        target={roomsAndRatesTarget}
      />
    );

    const refreshedLayout = (
      <ErrorBoundary label="hotel.information.details.information">
        <section className="all-x-padding-three">
          <div className="all-y-padding-three">
            <PropertyBadges vipMessage={vipMessage} propertyTypeBadge={propertyTypeBadge} />
            {hotelBasicInfo}
          </div>
          <UitkCard className={largeCardClassName}>
            <section className="uitk-cell uitk-card-media all-cell-2-3">{galleryComponent}</section>
          </UitkCard>
          <div className="uitk-cell all-cell-1-3 all-b-padding-three">{hotelInfo(false)}</div>
        </section>
      </ErrorBoundary>
    );

    return (
      <div itemScope={true} itemType="https://schema.org/Hotel">
        <Viewport>
          <ViewSmall>
            <React.Fragment>{isRefreshedHISLayout ? refreshedLayout : smallView}</React.Fragment>
          </ViewSmall>
          <ViewLarge>
            <React.Fragment>{isRefreshedHISLayout ? refreshedLayout : largeView}</React.Fragment>
          </ViewLarge>
        </Viewport>
      </div>
    );
  }
}
